import { useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowRight, Database, FileQuestion, Layers3, LocateFixed,
  MapPinned, Search, ShieldCheck,
} from 'lucide-react';
import type {
  GeologyBoreholeCensus, GeologyBoreholeRegistry, GeologyBoreholeRegistryRecord,
  GeologyBoreholeSpatialStatus, GeologyMapFeatures, MapBlock,
} from '../../lib/api/geologist';
import BoreholeMap from './BoreholeMap';
import type { GeoUiLang } from './geoWorkspaceI18n';

type SpatialFilter = 'all' | 'mapped' | 'context' | 'identifier';

const COPY = {
  ru: {
    title: 'Пространственные доказательства и реестр', subtitle: 'Карта и все индексированные скважины архива',
    warning: 'Google Maps показывает все устья, для которых в управляемом архиве есть печатные X/Y и явная кандидатная операция преобразования.',
    deposit: 'Для каталога 1975 года применяется практическая неопределённость 35 м. Это позиции для анализа архива, а не геодезически сертифицированные устья.',
    indexed: 'индексировано в 9 группах', mapped: 'кандидатных WGS84 устьев', notGlobal: 'без надёжной глобальной позиции',
    reported: 'итог кампании 1982', why: 'Почему не каждая архивная запись имеет точку на карте?',
    whyBody: 'Восстановленный каталог 1975 года добавил 277 устьев с кандидатными координатами. Строка 58 / скважина 82 остаётся без точки из-за нерешённой печатной аномалии Y; для остальных записей позиции не выдумываются.',
    browse: 'Архивная навигация', browseBody: 'Выберите любую запись, чтобы открыть её индивидуальное 3D-представление и доступные доказательства.',
    search: 'Номер скважины или год…', showing: 'показано', of: 'из', records: 'записей',
    all: 'Все записи', context: 'Локально / разрез', identifier: 'OCR-кандидаты', open3d: 'Открыть 3D',
    localCount: 'Контекстные записи сохраняются без искусственной привязки', identifierCount: 'OCR-кандидаты не объединяются с каталогом без доказательства',
    campaign: '206 — отдельный источник-итог, не дополнительные точки',
    campaignBody: 'Отчёт 1982 года сообщает 206 скважин кампании, но не содержит полного каталога идентификаторов. Этот источник-итог нельзя складывать с записями управляемого реестра или превращать в дополнительные точки без координат.',
    noResults: 'Записи не найдены.',
  },
  hy: {
    title: 'Տարածական ապացույցներ և գրանցամատյան', subtitle: 'Քարտեզը և արխիվի բոլոր ինդեքսավորված հորատանցքերը',
    warning: 'Google Maps-ը ցույց է տալիս բոլոր բերանները, որոնց համար վերահսկվող արխիվում կան տպագիր X/Y և հստակ թեկնածու փոխակերպման գործողություն։',
    deposit: '1975 թ. կատալոգի գործնական անորոշությունը 35 մ է։ Դրանք արխիվային վերլուծության դիրքեր են, ոչ գեոդեզիապես հավաստված բերաններ։',
    indexed: 'ինդեքսավորված՝ 9 խմբում', mapped: 'թեկնածու WGS84 բերան', notGlobal: 'առանց վստահելի գլոբալ դիրքի',
    reported: '1982 թ. արշավի արդյունք', why: 'Ինչո՞ւ յուրաքանչյուր արխիվային գրառում քարտեզային կետ չունի։',
    whyBody: 'Վերականգնված 1975 թ. կատալոգը ավելացրել է 277 թեկնածու բերան։ 58-րդ տողը / 82-րդ հորատանցքը չի քարտեզագրվում չլուծված տպագիր Y անոմալիայի պատճառով, իսկ մնացած դիրքերը չեն հորինվում։',
    browse: 'Արխիվային նավարկում', browseBody: 'Ընտրեք ցանկացած գրառում՝ դրա անհատական 3D ներկայացումն ու հասանելի ապացույցները բացելու համար։',
    search: 'Հորատանցքի համար կամ տարի…', showing: 'ցուցադրված է', of: '/', records: 'գրառում',
    all: 'Բոլոր գրառումները', context: 'Տեղական / կտրվածք', identifier: 'OCR թեկնածուներ', open3d: 'Բացել 3D',
    localCount: 'Համատեքստային գրառումները պահվում են առանց արհեստական կապակցման', identifierCount: 'OCR թեկնածուները առանց ապացույցի չեն միացվում կատալոգին',
    campaign: '206-ը առանձին աղբյուրային արդյունք է, ոչ լրացուցիչ կետեր',
    campaignBody: '1982 թ. հաշվետվությունը նշում է արշավի 206 հորատանցք, բայց ամբողջական նույնացուցիչների կատալոգ չի պարունակում։ Այս ամփոփիչ աղբյուրը չի կարելի գումարել կառավարվող ռեեստրի գրառումներին կամ առանց կոորդինատների դարձնել լրացուցիչ քարտեզային կետեր։',
    noResults: 'Գրառումներ չեն գտնվել։',
  },
  en: {
    title: 'Spatial evidence and borehole registry', subtitle: 'The map and every indexed archive borehole',
    warning: 'Google Maps shows every collar with printed X/Y evidence and an explicit candidate transformation in the governed archive.',
    deposit: 'The 1975 catalogue carries 35 m practical uncertainty. These positions support archive analysis; they are not survey-certified collars.',
    indexed: 'indexed across 9 groups', mapped: 'candidate WGS84 collars', notGlobal: 'without reliable global positions',
    reported: '1982 campaign total', why: 'Why does not every archive record have a map point?',
    whyBody: 'The recovered 1975 catalogue adds 277 candidate collars. Row 58 / borehole 82 remains unplaced because its printed Y value is unresolved; positions for the remaining records are not invented.',
    browse: 'Archive navigator', browseBody: 'Select any record to open its individual 3D evidence view and all available evidence.',
    search: 'Borehole number or year…', showing: 'showing', of: 'of', records: 'records',
    all: 'All records', context: 'Local / section', identifier: 'OCR candidates', open3d: 'Open 3D',
    localCount: 'Context records remain visible without artificial placement', identifierCount: 'OCR candidates are not merged into the catalogue without evidence',
    campaign: '206 is a separate source-reported total, not additional map points',
    campaignBody: 'The 1982 report states 206 campaign holes but does not provide the complete identifier catalogue. This source-level total cannot be added to the governed registry or turned into extra map points without coordinates.',
    noResults: 'No records match this filter.',
  },
} satisfies Record<GeoUiLang, Record<string, string>>;

const SPATIAL_COPY: Record<GeoUiLang, Record<GeologyBoreholeSpatialStatus, { label: string; reason: string }>> = {
  ru: {
    candidate_global: { label: 'Кандидатная WGS84', reason: 'Печатные X/Y/H преобразованы явной кандидатной операцией; для каталога 1975 года показана практическая неопределённость 35 м.' },
    cross_document_inferred: { label: 'Междокументная привязка', reason: '2 устья: сокращённые печатные координаты дополнены префиксами общей сетки 1961/1963; осторожность 10–30 м.' },
    ocr_candidate: { label: 'OCR-кандидаты', reason: 'Упоминания номеров извлечены из OCR и не объединены с новым каталогом без подтверждённой связи источников.' },
    borehole_log_no_collar: { label: 'Журнал без координат устья', reason: 'Для скважины восстановлены кандидатные интервалы геологического журнала, но нет доказанной глобальной координаты устья или инклинометрии.' },
    local_coordinates: { label: 'Локальные координаты', reason: 'Сохранились 2 локальные координатные тройки, но нет достаточного CRS/контроля для WGS84.' },
    identifier_only: { label: 'Только идентификаторы', reason: 'Есть номера скважин, но нет координат устьев, плана положения или геодезического контроля.' },
    section_only: { label: 'Только разрезы', reason: 'Скважины подтверждены на вертикальных разрезах; глобальные координаты устьев отсутствуют.' },
    local_grid_unresolved: { label: 'Непривязанная сетка', reason: 'Сохранилась локальная сетка, но её численные подписи и внешние контрольные точки недостаточны.' },
    section_or_plan_only: { label: 'План / разрез без привязки', reason: 'Идентификаторы видны на планах и разрезах, но для этих конкретных записей нет подтверждённой связи с координатным каталогом.' },
    projected_grid_crs_unknown: { label: 'CRS неизвестна', reason: 'Видна локальная/проекционная сетка, но её датум, зона и CRS не подтверждены.' },
  },
  hy: {
    candidate_global: { label: 'Թեկնածու WGS84', reason: 'Տպագիր X/Y/H-ը փոխակերպվել է հստակ թեկնածու գործողությամբ․ 1975 թ. կատալոգի համար ցուցադրվում է 35 մ գործնական անորոշություն։' },
    cross_document_inferred: { label: 'Միջփաստաթղթային կապակցում', reason: '2 բերան․ կրճատ տպագիր կոորդինատները լրացվել են 1961/1963 ընդհանուր ցանցի նախածանցներով, զգուշավորությունը՝ 10–30 մ։' },
    ocr_candidate: { label: 'OCR թեկնածուներ', reason: 'Համարները ստացվել են OCR-ից և առանց հաստատված աղբյուրային կապի չեն միացվել նոր կատալոգին։' },
    borehole_log_no_collar: { label: 'Մատյան՝ առանց բերանի կոորդինատի', reason: 'Հորատանցքի երկրաբանական մատյանի թեկնածու խորքային միջակայքերը վերականգնված են, սակայն գլոբալ բերանի կոորդինատ կամ շեղաչափական տվյալ չկա։' },
    local_coordinates: { label: 'Տեղական կոորդինատներ', reason: 'Պահպանվել է 2 տեղական կոորդինատային եռյակ, բայց WGS84-ի համար բավարար CRS/հսկիչ տվյալ չկա։' },
    identifier_only: { label: 'Միայն նույնացուցիչներ', reason: 'Համարները պահպանվել են, բայց բերանի կոորդինատ, տեղադիրք կամ գեոդեզիական հսկիչ տվյալ չկա։' },
    section_only: { label: 'Միայն կտրվածքներ', reason: 'Հորատանցքերը հաստատված են ուղղաձիգ կտրվածքներում, իսկ գլոբալ բերանների կոորդինատները բացակայում են։' },
    local_grid_unresolved: { label: 'Չկապակցված ցանց', reason: 'Տեղական ցանցը պահպանվել է, բայց թվային եզրային նշումներն ու արտաքին հսկիչ կետերը բավարար չեն։' },
    section_or_plan_only: { label: 'Հատակագիծ / կտրվածք', reason: 'Նույնացուցիչները կան, բայց այս գրառումների և կոորդինատների կատալոգի միջև հաստատված կապ չկա։' },
    projected_grid_crs_unknown: { label: 'Անհայտ CRS', reason: 'Տեղական/պրոյեկցիոն ցանցը տեսանելի է, բայց datum-ը, գոտին և CRS-ը հաստատված չեն։' },
  },
  en: {
    candidate_global: { label: 'Candidate WGS84', reason: 'Printed X/Y/H use an explicit candidate operation; the 1975 catalogue is displayed with 35 m practical uncertainty.' },
    cross_document_inferred: { label: 'Cross-document georeference', reason: 'Two collars restore shortened printed coordinates from the shared 1961/1963 grid; 10–30 m caution.' },
    ocr_candidate: { label: 'OCR candidates', reason: 'Identifier mentions come from OCR and are not merged into the new catalogue without a supported source correlation.' },
    borehole_log_no_collar: { label: 'Log without collar coordinates', reason: 'Candidate geological log intervals are recovered for this borehole, but no defensible global collar coordinate or deviation survey is available.' },
    local_coordinates: { label: 'Local coordinates', reason: 'Two local collar triples survive, but CRS/control is insufficient for WGS84.' },
    identifier_only: { label: 'Identifiers only', reason: 'Borehole numbers survive, but collar coordinates, a location plan, and survey control do not.' },
    section_only: { label: 'Sections only', reason: 'Boreholes are evidenced in vertical sections; global collar coordinates are absent.' },
    local_grid_unresolved: { label: 'Unresolved local grid', reason: 'A local grid survives, but numeric margins and external control are insufficient.' },
    section_or_plan_only: { label: 'Plan / section, uncorrelated', reason: 'Identifiers survive on plans and sections, but no supported link to a coordinate-catalogue record exists.' },
    projected_grid_crs_unknown: { label: 'Unknown CRS', reason: 'A local/projected grid is visible, but its datum, zone, and CRS are not defensible.' },
  },
};

function filterClass(record: GeologyBoreholeRegistryRecord): SpatialFilter {
  if (record.coordinate_status === 'candidate_global') return 'mapped';
  if (record.spatial_status === 'identifier_only' || record.spatial_status === 'ocr_candidate') return 'identifier';
  return 'context';
}

function nullableNumber(value: number | string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export default function GeologyArchiveMap({
  data, census, registry, lang, onOpenBorehole,
}: {
  data?: GeologyMapFeatures;
  census?: GeologyBoreholeCensus;
  registry?: GeologyBoreholeRegistry;
  lang: GeoUiLang;
  onOpenBorehole?: (record: GeologyBoreholeRegistryRecord) => void;
}) {
  const copy = COPY[lang];
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<SpatialFilter>('all');
  const block = useMemo<MapBlock>(() => {
    const registryPoints = (registry?.records ?? [])
      .filter((record) => record.collar?.latitude != null && record.collar?.longitude != null)
      .map((record) => {
        const collar = record.collar!;
        return {
          borehole_code: `${record.source_group}:${record.source_label}`,
          record_key: record.record_key,
          report_year: Number(record.source_group) || null,
          original_x_text: collar.raw_x,
          original_y_text: collar.raw_y,
          x_original: nullableNumber(collar.projected_x),
          y_original: nullableNumber(collar.projected_y),
          z_original: nullableNumber(collar.elevation_m),
          source_crs: collar.source_crs,
          longitude: collar.longitude,
          latitude: collar.latitude,
          linked_samples: [],
          depth_intervals: record.intervals.map((interval) => ({ ...interval })),
          properties: [],
          verification_status: collar.verification_status,
        };
      });
    const publishedPoints = (data?.collars ?? []).map((collar) => ({
      borehole_code: collar.printed_identifier,
      report_year: Number(collar.group_key) || null,
      original_x_text: collar.raw_x,
      original_y_text: collar.raw_y,
      x_original: nullableNumber(collar.projected_x),
      y_original: nullableNumber(collar.projected_y),
      z_original: collar.elevation_m,
      source_crs: collar.source_crs,
      longitude: collar.longitude,
      latitude: collar.latitude,
      linked_samples: [],
      depth_intervals: [],
      properties: [],
      verification_status: collar.verification_status,
    }));
    const points = registryPoints.length ? registryPoints : publishedPoints;
    return {
      status: points.length ? 'ready' : 'no_coordinates',
      points,
      source_crs_label: 'Candidate EPSG:28408 · Pulkovo 1942 / Gauss–Krüger zone 8',
      residual_error_m: 4.5,
      library: 'PostGIS',
      provisional: true,
      provisional_note: copy.warning,
    };
  }, [copy.warning, data, registry?.records]);

  const records = useMemo(() => registry?.records ?? [], [registry?.records]);
  const visible = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    return records.filter((record) => {
      if (filter !== 'all' && filterClass(record) !== filter) return false;
      return !needle || `${record.source_group} ${record.source_label}`.toLocaleLowerCase().includes(needle);
    });
  }, [filter, query, records]);
  const groups = useMemo(() => (registry?.source_group_summaries ?? []).map((summary) => ({
    summary,
    records: visible.filter((record) => record.source_group === summary.group_key),
  })).filter((group) => group.records.length), [registry?.source_group_summaries, visible]);

  const indexed = registry?.summary.indexed_records
    ?? census?.dataset_scoped_identifier_inventory.record_count ?? 0;
  const mapped = registry?.summary.map_ready_records ?? census?.map_ready_collars.count ?? data?.collars.length ?? 0;
  const notGlobal = Math.max(0, indexed - mapped);
  const campaign = census?.source_reported_campaign.holes_total
    ?? registry?.summary.source_reported_campaign_holes ?? 206;

  const filters: Array<{ id: SpatialFilter; label: string; count: number }> = [
    { id: 'all', label: copy.all, count: indexed },
    { id: 'mapped', label: copy.mapped, count: mapped },
    { id: 'context', label: copy.context, count: registry?.summary.local_or_section_records ?? 174 },
    { id: 'identifier', label: copy.identifier, count: registry?.summary.identifier_only_records ?? 238 },
  ];

  return (
    <section className="gv2-mode-panel gv2-map-panel" data-testid="geology-map-panel">
      <header className="gv2-mode-header">
        <div><span><MapPinned size={15} />GIS + ARCHIVE REGISTRY</span><h2>{copy.title}</h2><p>{copy.subtitle}</p></div>
        <span className="gv2-scope-pill"><ShieldCheck size={13} />{data?.knowledge_scope || 'published'}</span>
      </header>

      <div className="gv2-map-stats" aria-label="Borehole spatial coverage">
        <span><Database size={16} /><b>{indexed}</b><small>{copy.indexed}</small></span>
        <span className="is-mapped"><LocateFixed size={16} /><b>{mapped}</b><small>{copy.mapped}</small></span>
        <span className="is-context"><Layers3 size={16} /><b>{notGlobal}</b><small>{copy.notGlobal}</small></span>
        <span className="is-campaign"><FileQuestion size={16} /><b>{campaign}</b><small>{copy.reported}</small></span>
      </div>

      <div className="gv2-map-explainer">
        <AlertTriangle size={18} />
        <div><strong>{copy.why}</strong><p>{copy.whyBody}</p><small>{copy.localCount} · {copy.identifierCount}</small></div>
      </div>

      <div className="gv2-map-workbench">
        <div className="gv2-map-canvas-column">
          <div className="gv2-map-warning"><ShieldCheck size={16} /><div><strong>{copy.warning}</strong><span>{copy.deposit}</span></div></div>
          <BoreholeMap
            block={block}
            lang={lang}
            onOpenPoint={(point) => {
              const record = records.find(
                (item) => item.record_key === point.record_key
              );
              if (record) onOpenBorehole?.(record);
            }}
          />
          <div className="gv2-map-campaign-note"><FileQuestion size={16} /><div><strong>{copy.campaign}</strong><p>{copy.campaignBody}</p></div></div>
        </div>

        <aside className="gv2-map-registry" aria-label={copy.browse}>
          <header><div><span>{indexed} / ARCHIVE</span><h3>{copy.browse}</h3><p>{copy.browseBody}</p></div></header>
          <label className="gv2-map-search"><Search size={14} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={copy.search} /></label>
          <div className="gv2-map-filters" role="group" aria-label="Spatial status">
            {filters.map((item) => <button key={item.id} className={filter === item.id ? 'is-active' : ''} onClick={() => setFilter(item.id)} aria-pressed={filter === item.id}>
              {item.label}<span>{item.count}</span>
            </button>)}
          </div>
          <div className="gv2-map-registry-count">{copy.showing} <b>{visible.length}</b> {copy.of} {indexed} {copy.records}</div>
          <div className="gv2-map-registry-scroll">
            {groups.map(({ summary, records: groupRecords }) => {
              const spatial = SPATIAL_COPY[lang][summary.spatial_status];
              return <section key={summary.group_key} className={`gv2-map-source-group is-${summary.coordinate_status}`}>
                <header title={spatial.reason}>
                  <div><strong>{summary.group_key}</strong><span>{spatial.label}</span></div><b>{groupRecords.length}</b>
                  <p>{spatial.reason}</p>
                </header>
                <div>{groupRecords.map((record) => <button
                  key={record.record_key}
                  className={`is-${filterClass(record)} ${record.confidence === 'tentative' ? 'is-tentative' : ''}`}
                  onClick={() => onOpenBorehole?.(record)}
                  title={`${record.source_group} · ${record.source_label} · ${SPATIAL_COPY[lang][record.spatial_status].label}`}
                  aria-label={`${record.source_group} borehole ${record.source_label}, ${copy.open3d}`}
                ><span>{record.source_label}</span><ArrowRight size={11} /></button>)}</div>
              </section>;
            })}
            {!visible.length && <div className="gv2-map-no-results"><Search size={19} /><p>{copy.noResults}</p></div>}
          </div>
        </aside>
      </div>
    </section>
  );
}
